import {
  useCapTableTransactions,
  useCompanyData,
  useStockClasses,
} from '@commenda/api';
import { useGetAllDirectorsInCompany } from '@commenda/api/director';
import { getCorporationName, getPrintableDirectorName } from '@commenda/utils';
import { StakeholderType } from '@prisma/client';

export const useEquityResources = (
  companyId: number,
  corporationId: number
) => {
  const {
    data: directorsData,
    mutate: mutateDirectors,
    error: directorsDataError,
  } = useGetAllDirectorsInCompany(companyId);

  const {
    corporations,
    mutate: mutateCorporations,
    error: corporationsError,
  } = useCompanyData(companyId);

  const {
    data: capTableTransactions,
    error: capTableTransactionsError,
    mutate: mutateCapTableTransactions,
  } = useCapTableTransactions(corporationId);

  const { data: stockClasses, error: stockClassesError } =
    useStockClasses(corporationId);

  const stakeholderOptions: (
    | { type: 'INDIVIDUAL'; name: string; directorId: number; id: string }
    | { type: 'INSTITUTION'; name: string; corporationId: number; id: string }
  )[] = [];

  directorsData?.directors?.forEach((dir) => {
    stakeholderOptions.push({
      type: StakeholderType.INDIVIDUAL,
      name: getPrintableDirectorName(dir),
      directorId: dir.id,
      id: `INDIVIDUAL_${dir.id}`,
    });
  });

  corporations
    ?.filter((corp) => corp.id !== corporationId)
    ?.filter((corp) => {
      // Filter out corporations without legal names
      const name = getCorporationName(corp);
      return name && name.trim().length > 0;
    })
    ?.forEach((corp) => {
      stakeholderOptions.push({
        name: getCorporationName(corp),
        corporationId: corp.id,
        type: StakeholderType.INSTITUTION,
        id: `INSTITUTION_${corp.id}`,
      });
    });

  return {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    isLoading:
      !directorsData || !corporations || !capTableTransactions || !stockClasses,
    isError:
      directorsDataError ||
      corporationsError ||
      capTableTransactionsError ||
      stockClassesError,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
  };
};
